import FilledButtonM from "@common/components/button/FilledButtonM"
import TextM from "@common/components/text/TextM"
import { ScreenSkeleton } from "@common/screens/ScreenSkeleton"
import { theme } from "@common/theme/theme"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { useRequestOnMount } from "@common/hooks/useRequestOnMount"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { apiRequest } from "@data/redux/actions/api"
import { tutorAvailabilityService } from "@data/services/tutorAvailabilityService"
import { useNavigation } from "@react-navigation/native"
import moment from "moment"
import "moment/locale/fr"
import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { ActivityIndicator, ScrollView, View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { connect } from "react-redux"
import IconButtonM from "@common/components/button/IconButtonM"
import TextButtonM from "@common/components/button/TextButtonM"
import TitleM from "@common/components/text/TitleM"
import { HeaderContainerM } from "@common/header/HeaderContainerM"
import { MaDispoContinuitySheet } from "./MaDispoContinuitySheet"
import { GUTTER, MaDispoEditor } from "./MaDispoEditor"
import { MaDispoInfoSheet } from "./MaDispoInfoSheet"
import { showEngagedSlotWarning } from "./maDispoDialogs"
import { AVAILABILITY_FRESHNESS, slotKeyOf, slotStartOf } from "./maDispoSlot"

/** Order- and padding-insensitive signature of a slot list ({@see slotKeyOf}). */
const canonicalSlots = (list) => [...list].map(slotKeyOf).sort().join("|")

// The freshness banner's four mutually exclusive states. Kept as one variant
// rather than a pair of booleans so « not loaded » can never be confused with
// « loaded and empty » — they must not render the same copy.
//
// None of them is about the date de démarrage any more: validating a week now
// DATES it when the floor is absent or behind us, so a banner asking for that date
// would name a condition nothing enforces.
//
// LOADING is currently unreachable: the ScreenSkeleton below renders the body only
// once `week` is in hand (or shows the error screen). It is kept deliberately as
// the safety net for that invariant — the alternative to a neutral copy for a null
// week is a screen that tells the tutor they have no créneau, and the cost of being
// wrong there is higher than an unused branch. Do not fold it into NEEDS_SLOTS.
const BANNER_VERIFIED = "verified"
const BANNER_LOADING = "loading"
const BANNER_NEEDS_CONFIRM = "needs_confirm"
const BANNER_NEEDS_SLOTS = "needs_slots"

/**
 * « Ajouter mes créneaux disponibles » — the tutor semaine-type screen.
 *
 * Two numbered steps: ① the availability starting date, ② the week as a grid of
 * hours the tutor taps to take or drop ({@see MaDispoEditor}).
 *
 * Freshness is a WEEK-level stamp, and ONE surface says so: the banner. It used
 * to be echoed on every posed créneau as an orange dot, which announced a
 * per-créneau problem for a state that is confirmed — and can only be confirmed —
 * for the whole week at once. The hours now say what they ARE (taken, live,
 * free); the banner says whether the week is still fresh.
 *
 * ONE write, too: the sticky bottom CTA. The banner carried a second
 * « Confirmer » doing the same save 400 px higher up.
 */
const MaDispoScreen = (props) => {
    const navigation = useNavigation()
    const dimensions = useContext(DimensionsContext)

    const week = props.weekView
    // The bookable grid comes from the backend (canonical source of truth).
    const grid = week?.grid ?? {}

    // ── Local draft ─────────────────────────────────────────────────────────
    // Adding or removing a créneau writes NOTHING: it edits a draft seeded from
    // the server week. « Confirmer ma dispo » is the only save, and leaving with a
    // dirty draft raises that same confirmation. Engaged créneaux are never in
    // the draft — they are not the tutor's to move from here.
    const serverOpenSlots = useMemo(
        () => (week?.slots ?? []).filter((s) => !s.engaged).map((s) => s.slot),
        [week]
    )
    // A créneau the tutor already freed shows freed: its live only runs until it ends.
    const engagedSlots = useMemo(
        () =>
            (week?.slots ?? [])
                .filter((s) => s.engaged)
                .map((s) => (s.released ? { ...s, freed: true } : s)),
        [week]
    )
    // `null` = « not seeded yet ». Kept distinct from « seeded and empty » so a
    // tutor who removes their last créneau still holds a dirty, saveable draft.
    const [draftOpenSlots, setDraftOpenSlots] = useState(null)
    useEffect(() => {
        if (week && draftOpenSlots === null) setDraftOpenSlots(serverOpenSlots)
    }, [week, draftOpenSlots, serverOpenSlots])

    const openSlots = draftOpenSlots ?? serverOpenSlots
    const slots = useMemo(
        () => [
            ...openSlots.map((slot) => ({
                slot,
                state: "open",
                engaged: false,
            })),
            ...engagedSlots,
        ],
        [openSlots, engagedSlots]
    )
    // Order-insensitive, start-keyed: the same week posed in another order is
    // not an unsaved change.
    const isDirty =
        draftOpenSlots !== null &&
        canonicalSlots(draftOpenSlots) !== canonicalSlots(serverOpenSlots)

    useRequestOnMount(
        props.apiRequest,
        tutorAvailabilityService.getAvailability,
        undefined,
        // Re-seed from the week that just landed. The week view is redux-persisted,
        // so the first render carries LAST session's one: kept as the draft, it
        // hides a créneau the server has gained since and the replace-set save
        // then deletes it.
        () => setDraftOpenSlots(null)
    )
    // The week view is the whole screen, so there is nothing truthful to render
    // before it lands: with `week` null every derived fact reads as « empty ».
    // useRequestOnMount's own `initialLoadingHack` is a fixed 500 ms timer, not
    // tied to the request, so it would hand a slow fetch a screen that asserts
    // the tutor has no créneau. Gate on the data instead — and let a failed fetch
    // surface the error screen rather than spinning forever.
    const weekLoaded = !!week

    const goBack = () => {
        if (navigation.canGoBack()) navigation.goBack()
        else navigation.replace("menu")
    }
    // Where both sticky-CTA states land: « Confirmer ma dispo » and « Tableau de
    // bord » alike promise the dashboard, so they go THERE — not back to wherever
    // the tutor came from (Mon compte, Offres de cours, the monthly reminder
    // popup). `navigate` pops back to the tab shell when it already sits in the
    // stack rather than pushing a second one.
    const goToDashboard = () => navigation.navigate("menu")

    // Raised right before an authorised exit so the unsaved-changes guard below
    // does not intercept the very navigation it just allowed. `pendingLeave`
    // holds the action the guard blocked, so the tutor lands where they aimed.
    const bypassLeaveGuardRef = useRef(false)
    const pendingLeaveRef = useRef(null)
    // `fallback` is where to go when the guard blocked nothing: the dashboard after
    // a « Confirmer ma dispo », which is what the CTA promises whichever of the four
    // entry points the tutor arrived from; plain « back » when the tutor is the one
    // who asked to leave, since then they aimed somewhere themselves.
    const leaveScreen = (fallback = goBack) => {
        bypassLeaveGuardRef.current = true
        const pending = pendingLeaveRef.current
        pendingLeaveRef.current = null
        if (pending) navigation.dispatch(pending)
        else fallback()
    }
    useApiStatus(tutorAvailabilityService.confirmWeek, "", true, () => {
        // Re-seed from the week the save returned — the draft is no longer ahead.
        setDraftOpenSlots(null)
        // One confirmation, one destination. The banner used to carry a second
        // « Confirmer » that saved in place, so a ref had to tell the two apart;
        // it was the same action as the sticky CTA, offered twice on one screen.
        leaveScreen(goToDashboard)
    })
    useApiStatus(tutorAvailabilityService.updateStartingDate)

    // Back arrow, hardware back and any programmatic exit all funnel here: an
    // unsaved draft raises its own sheet, which offers the same save.
    useEffect(() => {
        return navigation.addListener("beforeRemove", (e) => {
            if (!isDirty || bypassLeaveGuardRef.current) return
            e.preventDefault()
            pendingLeaveRef.current = e.data.action
            setContinuitySheet("leave")
        })
    }, [navigation, isDirty])

    // ── Sheets / dialogs local state ────────────────────────────────────────
    const [pendingStart, setPendingStart] = useState(null)
    // null | "add" (acknowledge after a créneau is posed) | "confirm" (the CTA)
    // | "leave" (the same sheet, raised by the unsaved-changes guard).
    const [continuitySheet, setContinuitySheet] = useState(null)
    // Show the continuity warning only once per session on the first add.
    const [continuityShownThisSession, setContinuityShownThisSession] =
        useState(false)

    const freshness = week?.freshness ?? AVAILABILITY_FRESHNESS.TO_CONFIRM
    // Unsaved edits un-verify the week on the spot: the green state describes
    // what the families will be offered, which is the SAVED week, not the draft.
    const isVerified = freshness === AVAILABILITY_FRESHNESS.VERIFIED && !isDirty
    // The backend's own verdict, NOT `slots.length`: that list also carries engaged
    // créneaux the tutor never declared (a Sunday cours particulier can't even be
    // declared — it is outside the régime scolaire grid), and only the DECLARED half
    // can be stamped. Counting `slots` let such a week enable « Valider », save
    // nothing, and send the tutor back to a dashboard still nudging them.
    const hasSlots = week?.has_declared_slots ?? false
    // What the CTA may post. An untouched week the back end cannot stamp is not it:
    // the save would bounce the tutor back to the dashboard having changed nothing
    // while the nudges kept firing. But a week the tutor EMPTIED on purpose is a
    // real change — it withdraws them from the pool — so a dirty draft stays
    // saveable even when it ends up empty.
    const canSave = hasSlots || isDirty

    // « not loaded yet » and « loaded, and empty » are different facts and must not
    // share copy: only the second one may tell the tutor they have posed nothing.
    const bannerVariant = isVerified
        ? BANNER_VERIFIED
        : !weekLoaded
          ? BANNER_LOADING
          : !hasSlots
            ? BANNER_NEEDS_SLOTS
            : BANNER_NEEDS_CONFIRM

    // ── Actions ─────────────────────────────────────────────────────────────
    // The ONLY write of the semaine-type: the draft is posted whole (a
    // replace-set), then stamped « vérifiée » in the same request.
    const doConfirmWeek = () => {
        if (!canSave) return
        props.apiRequest(
            tutorAvailabilityService.confirmWeek,
            {},
            { slots: openSlots.map(slotStartOf) }
        )
    }

    // Confirming the week on the régime scolaire re-surfaces the continuity
    // engagement warning; the tutor validates it before the stamp is posed.
    const handleConfirmWeek = () => {
        if (isVerified) {
            goToDashboard()
            return
        }
        setContinuitySheet("confirm")
    }

    const handleAddSlot = (day, time) => {
        const slot = `${day} ${time}`
        setDraftOpenSlots((current) => {
            const base = current ?? serverOpenSlots
            return base.some((s) => slotKeyOf(s) === slotKeyOf(slot))
                ? base
                : [...base, slot]
        })
        // First créneau posed this session → surface the continuity warning.
        if (!continuityShownThisSession) {
            setContinuityShownThisSession(true)
            setContinuitySheet("add")
        }
    }

    // No confirmation dialog: the week is a DRAFT until « Confirmer ma dispo »,
    // so dropping an hour is undone by tapping it again. Asking « Retirer ce
    // créneau ? » guarded a change that costs one tap to reverse, and put a modal
    // between the tutor and every edit.
    const handleRemove = (slot) => {
        setDraftOpenSlots((current) =>
            (current ?? serverOpenSlots).filter(
                (s) => slotKeyOf(s) !== slotKeyOf(slot.slot)
            )
        )
    }

    const openRelease = (slot) =>
        navigation.push(
            "tutor/liberer-creneaux",
            slot?.slot ? { slot: slot.slot } : undefined
        )

    // Starting date: free-form picker → warning → apply.
    const handlePickStart = (dateStr) => {
        if (!dateStr || dateStr === week?.availability_starting_date) return
        setPendingStart(dateStr)
    }
    const applyStart = () => {
        const value = pendingStart
        setPendingStart(null)
        props.apiRequest(
            tutorAvailabilityService.updateStartingDate,
            {},
            { availability_starting_date: value }
        )
    }

    // Three states, so it reads better named than as a nested ternary in the JSX.
    // While the week is still loading this stays on the neutral label (the button is
    // disabled either way) — nothing may assert an empty week before we know.
    // Keep them short: paper truncates a button label to one line, and no prop or
    // style can un-truncate it (`wrapText` is web-only, silently inert on native).
    const stickyCtaLabel = isVerified
        ? "Tableau de bord"
        : bannerVariant === BANNER_NEEDS_SLOTS && !isDirty
          ? "Ajoute un créneau"
          : "Confirmer ma dispo"

    return (
        <>
            {/* HeaderContainerM directly, not the HeaderM of the student account
                navigator: that one forces `width: "100%"`, which overrides the
                `dimensions.width` column and left the title flush to the screen
                edge while the body sat in a centred column — visibly out of line
                on web. It also rendered an IconButtonM with no icon on the right,
                a 48 px touch target that does nothing. */}
            <HeaderContainerM style={{ justifyContent: "flex-start" }}>
                <IconButtonM
                    icon="arrow-left"
                    iconColor={theme.colors.primary}
                    size={30}
                    onPress={goBack}
                />
                <TitleM>Ma dispo</TitleM>
            </HeaderContainerM>

            <ScreenSkeleton
                isLoading={!weekLoaded && !props.loadError}
                // Only when there is nothing to fall back on: a failed REFRESH
                // must not blank out a week view we already hold.
                errorScreenProps={{
                    error: weekLoaded ? null : props.loadError,
                    title: "Impossible de charger ta dispo",
                    onButtonPress: () =>
                        props.apiRequest(
                            tutorAvailabilityService.getAvailability
                        ),
                }}
            >
                <ScrollView
                    style={{ width: dimensions?.width, alignSelf: "center" }}
                    contentContainerStyle={{ paddingBottom: 32 }}
                >
                    <View style={{ paddingHorizontal: GUTTER, marginTop: 20 }}>
                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                            }}
                        >
                            <TextM
                                fontSize={21}
                                fontWeight="Bold"
                                wrap
                                style={{ flex: 1, lineHeight: 28 }}
                            >
                                Ajouter mes créneaux disponibles
                            </TextM>
                            {/* The REFRESH case only: a cold load never reaches
                                here (ScreenSkeleton holds the full-screen
                                indicator), but the week view is redux-persisted,
                                so a return visit renders LAST session's week
                                while the fetch is still in flight. */}
                            {props.weekLoading && (
                                <ActivityIndicator
                                    animating
                                    size="small"
                                    color={theme.colors.primary}
                                    style={{ marginLeft: 10 }}
                                />
                            )}
                        </View>
                        <TextM
                            fontSize={13.5}
                            color={theme.colors.light}
                            wrap
                            style={{ marginTop: 6, lineHeight: 19 }}
                        >
                            Indique quand et à quels créneaux tu peux prendre de
                            nouveaux élèves.
                        </TextM>
                    </View>

                    <FreshnessBanner variant={bannerVariant} />

                    <MaDispoEditor
                        slots={slots}
                        grid={grid}
                        startingDate={week?.availability_starting_date}
                        onPickStartingDate={handlePickStart}
                        onAddSlot={handleAddSlot}
                        onRemoveSlot={handleRemove}
                        onPressEngaged={(slot) =>
                            showEngagedSlotWarning(slot, openRelease)
                        }
                    />

                    {engagedSlots.some((s) => !s.released) && (
                        <View
                            style={{
                                paddingHorizontal: GUTTER,
                                marginTop: 18,
                                alignItems: "center",
                            }}
                        >
                            <TextButtonM
                                label="Je ne peux plus assurer un de mes créneaux"
                                fontWeight="Bold"
                                color={theme.colors.link}
                                onPress={() => openRelease()}
                                numberOfLines={2}
                                style={{ fontSize: 13, textAlign: "center" }}
                            />
                        </View>
                    )}
                </ScrollView>

                {/* Sticky bottom CTA (dispo-editor pattern). */}
                <View
                    style={{
                        paddingHorizontal: GUTTER,
                        paddingTop: 12,
                        paddingBottom: 16,
                        backgroundColor: theme.colors.white,
                        borderTopWidth: 1,
                        borderTopColor: theme.colors.lightMore,
                    }}
                >
                    <FilledButtonM
                        label={stickyCtaLabel}
                        icon={isVerified ? "check" : null}
                        loading={props.confirmLoading}
                        // Nothing to validate yet — an enabled CTA here would
                        // claim a confirmation the back end cannot record.
                        disabled={!canSave}
                        onPress={handleConfirmWeek}
                        style={{ marginBottom: 0 }}
                    />
                </View>
            </ScreenSkeleton>

            {/* Starting-date change warning (the picker itself lives in the editor). */}
            <MaDispoInfoSheet
                visible={!!pendingStart}
                onClose={() => setPendingStart(null)}
                title="Changer ta date de démarrage ?"
                body={
                    <TextM
                        wrap
                        fontSize={14}
                        color={theme.colors.grey}
                        style={{ textAlign: "center", lineHeight: 21 }}
                    >
                        Tes créneaux ne seront proposés aux familles qu'à partir
                        du{" "}
                        <TextM fontWeight="Bold" fontSize={14}>
                            {pendingStart
                                ? moment(pendingStart).format(
                                      "dddd D MMMM YYYY"
                                  )
                                : ""}
                        </TextM>
                        . Tes lives en cours ne sont pas concernés.
                    </TextM>
                }
                confirmLabel="Confirmer"
                onConfirm={applyStart}
                loading={props.startingDateLoading}
            />

            {/* Continuity engagement warning (first slot posed + validation). */}
            <MaDispoContinuitySheet
                visible={
                    continuitySheet === "add" || continuitySheet === "confirm"
                }
                onClose={() => setContinuitySheet(null)}
                confirmLabel={
                    continuitySheet === "add"
                        ? "J'ai compris"
                        : "Confirmer ma dispo"
                }
                // The « add » branch fires AFTER the créneau landed in the draft, so
                // it is an acknowledgement — leaving onConfirm unset renders the
                // single « J'ai compris » button, and no « Annuler » that would
                // pretend to undo the add.
                onConfirm={
                    continuitySheet === "add"
                        ? undefined
                        : () => {
                              setContinuitySheet(null)
                              doConfirmWeek()
                          }
                }
                loading={props.confirmLoading}
            />

            {/* Unsaved draft on the way out. Its OWN sheet, not the continuity one:
                the tutor pressed « back », so the question is what happens to their
                edits — being told again what a recurring créneau commits them to
                answers something they did not ask. */}
            <MaDispoInfoSheet
                visible={continuitySheet === "leave"}
                // Dismissing keeps the tutor here, so the navigation the guard
                // blocked is dropped with the sheet — otherwise a later confirm
                // would replay that stale action instead of the exit it asked for.
                onClose={() => {
                    pendingLeaveRef.current = null
                    setContinuitySheet(null)
                }}
                title="Enregistrer tes modifications ?"
                body="Tes créneaux ont changé. Sans confirmation, les familles continueront de voir ta semaine précédente."
                confirmLabel="Confirmer ma dispo"
                onConfirm={() => {
                    setContinuitySheet(null)
                    doConfirmWeek()
                }}
                // The cancel is the ESCAPE HATCH, not a « stay »: closing the sheet
                // (× / backdrop) is what keeps the tutor here.
                //
                // « Ne pas enregistrer » and not « Quitter sans enregistrer »: paper
                // truncates a button label to one line whatever the style, and the
                // longer form died as « QUITTER SANS ENRE… » on a 320 px screen —
                // the width where the tutor most needs to know what the button does.
                cancelLabel="Ne pas enregistrer"
                onCancel={() => {
                    setContinuitySheet(null)
                    setDraftOpenSlots(null)
                    leaveScreen()
                }}
                loading={props.confirmLoading}
            />
        </>
    )
}

/**
 * The orange banner's copy, kept as data so it is reviewable in one place — this PR
 * has re-worded it several times, and the strings are load-bearing (they must not
 * claim the lot-2 attribution mechanism is already active, and must not assert a
 * fact about the tutor's semaine before it has loaded).
 */
const VERIFIED_COPY = {
    title: "Tes créneaux sont confirmés",
    body: "À refaire la semaine prochaine.",
}
const NEEDS_CONFIRM_COPY = {
    title: "Confirme tes créneaux",
    body: "Une fois par semaine, pour continuer à recevoir des propositions.",
}
const NEEDS_SLOTS_COPY = {
    title: "Ajoute tes créneaux",
    body: "Tu n'en as encore posé aucun, donc on ne peut rien te proposer.",
}

/**
 * `LOADING` renders the neutral confirm copy with no action: without the week view
 * we do not know whether the tutor has créneaux, so neither « confirme » nor
 * « ajoute » is a claim we can make — and there is nothing to confirm anyway.
 */
/**
 * The ONE surface carrying week freshness — and it only tells, it does not act:
 * the sticky CTA below is the single save, and a second « Confirmer » here was
 * the same gesture offered twice on one screen.
 *
 * « À confirmer » is weekly hygiene, not a fault, so it reads in the app's own
 * blue rather than the warning orange it used to greet the tutor with. The alarm
 * tone is kept for the one state that really costs something — a week with no
 * créneau at all, which no family can be offered.
 */
const FreshnessBanner = ({ variant }) => {
    const tone =
        variant === BANNER_VERIFIED
            ? {
                  bg: theme.colors.successLight,
                  border: "#c3efdd",
                  icon: "check-circle",
                  iconColor: theme.colors.success,
              }
            : variant === BANNER_NEEDS_SLOTS
              ? {
                    bg: theme.colors.warningLight,
                    border: "#ffe2c2",
                    icon: "exclamation-circle",
                    iconColor: theme.colors.warning,
                }
              : {
                    bg: theme.colors.primaryLighter,
                    border: "#dfe6fb",
                    icon: "sync-alt",
                    iconColor: theme.colors.primary,
                }

    const copy =
        variant === BANNER_VERIFIED
            ? VERIFIED_COPY
            : variant === BANNER_NEEDS_SLOTS
              ? NEEDS_SLOTS_COPY
              : NEEDS_CONFIRM_COPY

    return (
        <View
            style={{
                marginHorizontal: GUTTER,
                marginTop: 20,
                flexDirection: "row",
                alignItems: "flex-start",
                backgroundColor: tone.bg,
                borderWidth: 1,
                borderColor: tone.border,
                borderRadius: 14,
                paddingVertical: 14,
                paddingHorizontal: 14,
            }}
        >
            <FontAwesome5
                name={tone.icon}
                size={17}
                color={tone.iconColor}
                style={{ marginRight: 12, marginTop: 1 }}
            />
            <View style={{ flex: 1 }}>
                <TextM fontWeight="Bold" fontSize={14} wrap>
                    {copy.title}
                </TextM>
                <TextM
                    fontSize={13}
                    color={theme.colors.grey}
                    wrap
                    style={{ marginTop: 3, lineHeight: 18 }}
                >
                    {copy.body}
                </TextM>
            </View>
        </View>
    )
}

const mapStateToProps = (state) => ({
    weekView: state.data.tutorAvailability?.weekView,
    loadError: state.api.getAvailability?.error,
    weekLoading: state.api.getAvailability?.request,
    confirmLoading: state.api.confirmWeek?.request,
    startingDateLoading: state.api.updateStartingDate?.request,
})

export default connect(mapStateToProps, { apiRequest })(MaDispoScreen)
